<template>
  <div>
    <PageAllow controller="Purchases" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-sheet class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="12" md="4">
            <v-row dense align="center" class="pa-1">
              <SearcherDatetimeRangeWithRadio
                v-model="options"
                type="datetime"
                show-radios
                :radios="datetimeRadios"
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}</v-icon>
              </v-btn>
              <v-spacer />
              <v-btn
                small
                color="primary"
                dark
                class="ms-1"
                @click.stop="$router.push('/purchases/new')"
              >
                <v-icon x-small class="pe-1">fa-plus</v-icon>
                {{ $t("PurchasesList.Fields.add") }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <!-- 采购单号 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="purchase_code"
                    v-model="options.purchase_code"
                    @text="purchaseCodeKeyword = $event"
                    :label="$t('PurchasesList.Fields.purchase_code')"
                  />
                </v-col>
                <!-- 第三方单号 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherKeyword
                    ref="ext_code"
                    v-model="options.ext_code"
                    @text="purchaseKeyword = $event"
                    :label="$t('PurchasesList.Fields.purchase')"
                  />
                </v-col>
                <!-- 仓库中心 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherStocks
                    v-model="stocksSelected"
                    :types="[0]"
                    :status="[1]"
                    :stockIds="settings.self_purchase_stocks"
                  />
                </v-col>
                <!-- 商品 -->
                <v-col cols="12" sm="6" md="4">
                  <SearcherMaterials v-model="options.material_ids" returnId />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <!-- 发货状态 -->
                  <SearcherStatus
                    v-model="options.delivery_flgs"
                    :label="$t('PurchasesList.deliveryFlgs.ObjectName')"
                    :items="$consts.purchaseDeliverFlgs"
                    :disable="disabledStatus"
                  />
                </v-col>
                <!-- 状态 -->
                <SearcherStatus
                  v-model="options.status"
                  :label="$t('PurchasesList.status.ObjectName')"
                  :items="$consts.purchaseStatus"
                />
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-sheet>
      <!-- 表格 -->
      <div class="mx-4 mb-4">
        <v-data-table
          :headers="Headers"
          :items="purchases"
          :options.sync="options"
          :loading="loading"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          mobile-breakpoint="800"
          multi-sort
          class="elevation-2 px-4 pt-1"
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.purchase_code="{ item }">
            <span
              class="text-no-wrap caption font-weight-bold"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.purchase_code }}
            </span>
            <br />
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.created | formatTime }}
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <PurchasesActions
              :purchase="item"
              showView
              :showCancel="item.actions && item.actions.cancel"
              :showCheck="item.actions && item.actions.check"
              :showDelivery="item.actions && item.actions.delivery"
              @done="getPurchasesFromAPI()"
            />
          </template>
          <template v-slot:item.total_num="{ item }">
            {{ item.total_num | formatTwoDot }}
          </template>
          <template v-slot:item.check_flg="{ item }">
            <v-chip
              small
              label
              :color="item.check_flg | checkFlgsColor"
              :text-color="item.check_flg | checkFlgsTextColor"
            >
              {{ item.check_flg | checkFlgsText }}
            </v-chip>
          </template>
          <template v-slot:item.delivery_flg="{ item }">
            <v-chip
              small
              label
              :color="item.delivery_flg | deliverFlgsColor"
              :text-color="item.delivery_flg | deliverFlgsTextColor"
            >
              {{ item.delivery_flg | deliverFlgsText }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="item.status | purchaseStatusColor"
              :text-color="item.status | purchaseStatusTextColor"
            >
              {{ item.status | purchaseStatusText }}
            </v-chip>
          </template>
          <template v-slot:item.hope_delivery_date="{ item }">
            {{ item.hope_delivery_date | formatDate }}
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>


<script>
import PageAllow from "@/components/PageAllow";
import i18n from "@/i18n";
import { mapState } from "vuex";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherDatetimeRangeWithRadio from "@/components/plugins/SearcherDatetimeRangeWithRadio";
import Purchases from "@/api/purchases";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStocks from "@/components/plugins/SearcherStocks";
import SearcherMaterials from "@/components/plugins/SearcherMaterials";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import PurchasesActions from "./plugins/Actions";

const defaultOptions = {
  datetimeRadioSelected: "created",
  created_time_from:
    new Date(new Date().getTime() - 7 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  complete_time_from: "",
  complete_time_to: "",
  purchase_code: "",
  material_ids: [],
  stock_ids: [],
  ext_code: "",
  origin_flgs: [104],
  status: [1, 2, 3, 4],
  delivery_flgs: [],
};

export default {
  data: () => ({
    purchases: [],
    paging: {},
    options: JSON.parse(JSON.stringify(defaultOptions)),
    loading: false,
    importFile: null,
    currentItem: null,
    editLogisticAlert: false,
    purchaseCodeKeyword: "",
    purchaseKeyword: "",
    stocksSelected: [],
  }),
  components: {
    Breadcrumbs,
    SearcherDatetimeRangeWithRadio,
    PageAllow,
    SearcherKeyword,
    SearcherStocks,
    SearcherMaterials,
    SearcherStatus,
    PurchasesActions,
  },
  props: {
    detailMode: Boolean,
  },
  computed: {
    disabledStatus() {
      // 仅 等待验收 时 发货状态可选
      if (
        this.options.status &&
        this.options.status.length == 1 &&
        this.options.status[0] == 2
      ) {
        return false;
      } else {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.options.delivery_flgs = [];
        return true;
      }
    },
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.purchases"),
        disabled: false,
        href: "/purchases",
      },
      {
        text: i18n.t("Nav.purchasesList"),
        disabled: true,
        href: "/purchases",
      },
    ],
    datetimeRadios: () => [
      {
        key: "created",
        text: i18n.t("PurchasesList.Fields.created"),
      },
      {
        key: "complete",
        text: i18n.t("PurchasesList.Fields.complete_date"),
      },
    ],
    Headers() {
      return [
        {
          text: i18n.t("Delivery.Fields.purchase_code"),
          align: "start",
          sortable: false,
          value: "purchase_code",
        },
        {
          text: i18n.t("PurchasesList.Fields.stock_name"),
          align: "start",
          class: "text-no-wrap",
          width: "100",
          sortable: false,
          value: "stock.name",
        },
        {
          text: i18n.t("Delivery.Fields.expect_date"),
          align: "start",
          sortable: false,
          value: "hope_delivery_date",
        },
        {
          text: i18n.t("Delivery.Fields.number"),
          align: "end",
          sortable: false,
          value: "total_num",
        },
        {
          text: i18n.t("Delivery.Fields.total"),
          align: "end",
          sortable: false,
          value: "total",
        },
        {
          text: i18n.t("Delivery.Fields.check_status"),
          align: "start",
          sortable: false,
          value: "check_flg",
        },
        {
          text: i18n.t("Delivery.Fields.status"),
          align: "start",
          sortable: false,
          value: "delivery_flg",
        },
        {
          text: i18n.t("PurchasesList.Fields.status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "status",
        },
        // 操作
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },
    ...mapState([
      "accountInfo",
      "settings",
      "accountUser",
      "fixHeaderHeightInPx",
      "mainContentScrolled",
    ]),
  },

  mounted() {
    this.$commonUtils.loadOptions("purchases", ["stocksSelected"]).then(
      function (data) {
        if (data.options) {
          this.options = data.options;
        } else this.resetSearch();
        data.selecteds.forEach((selected) => {
          switch (selected.key) {
            case "stocksSelected":
              this.stocksSelected = selected.value;
              break;
          }
        });
      }.bind(this)
    );
  },
  methods: {
    onSearch() {
      this.options = Object.assign(this.options, {
        purchase_code: this.purchaseCodeKeyword,
        ext_code: this.purchaseKeyword,
      });
      this.getPurchasesFromAPI();
    },
    resetSearch() {
      this.stocksSelected = [];
      var options = JSON.parse(JSON.stringify(defaultOptions));
      this.options = options;
      [
        this.$refs.purchase_code,
        this.$refs.ext_code,
        this.$refs.order_code,
        this.$refs.district_keyword,
      ].forEach((k) => {
        if (k) k.clear();
      });
    },
    getPurchasesFromAPI() {
      this.loading = true;
      Purchases.fetch(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.purchases = response.data.purchases;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
          this.loading = false;
        }.bind(this)
      );
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        sessionStorage.setItem(
          "purchases-options",
          JSON.stringify(this.options)
        );

        this.getPurchasesFromAPI();
      },
    },
    // 仓配中心搜索
    stocksSelected: {
      handler() {
        this.options.stock_ids = this.stocksSelected
          ? this.stocksSelected.map((e) => {
              return e.id;
            })
          : "";
        sessionStorage.setItem(
          "purchases-stocksSelected",
          JSON.stringify(this.stocksSelected)
        );
      },
    },
  },
};
</script>

<style>
</style>